import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useConfig } from '@occc/config';
import { Feature } from '@occc/utils-access-control';
import { RetailerConnectIcon } from '@occc-app/design-system/icons';
import { NestedNav } from '@occc-app/design-system/components/Sidebar/NestedNav';
import { NavigationSubItem } from '@occc-app/design-system/components/Sidebar/types';

import { routeCreator as retailerNetworkCreator } from '../retailer-connect.utils';
import { RETAILER_CONNECT_BASE_URL, RETAILER_CONNECT_TITLE } from '../retailer-connect.constants';
import { useRetailerConnectUser } from '../hooks/use-retailer-connect-user.hook';

export const RetailerNetworkSidebar = () => {
  const isRetailerConnectEnabled = useConfig('retailerConnect.enabled');
  const isTobaccoPerformanceEnabled = useConfig('retailerConnectTobaccoPerformance.enabled');

  const { isRetailerNetworkSiteUserRole } = useRetailerConnectUser();

  const { networkId = '' } = useParams<{ networkId: string }>();

  const items = useMemo(() => {
    if (isRetailerNetworkSiteUserRole) {
      return [
        {
          title: 'Overview',
          location: networkId
            ? retailerNetworkCreator.overview(networkId)
            : retailerNetworkCreator.overviewDefault(),
          accessControlKey: Feature.RETAILER_NETWORK_SITE_ROLES_HOME_PAGE_VIEW,
        },
        {
          title: 'My Stores',
          location: networkId
            ? retailerNetworkCreator.myStores(networkId)
            : retailerNetworkCreator.myStoresDefault(),
          accessControlKey: Feature.RETAILER_NETWORK_SITE_ROLES_HOME_PAGE_VIEW,
        },
      ];
    }

    const overviewItem: NavigationSubItem = {
      title: 'Overview',
      location: networkId
        ? retailerNetworkCreator.overview(networkId)
        : retailerNetworkCreator.overviewDefault(),
      accessControlKey: Feature.RETAILER_NETWORKS_VIEW,
    };

    const myStoresItem: NavigationSubItem = {
      title: 'My Stores',
      location: networkId
        ? retailerNetworkCreator.myStores(networkId)
        : retailerNetworkCreator.myStoresDefault(),
      accessControlKey: Feature.RETAILER_NETWORKS_VIEW,
    };

    const tobaccoItem: NavigationSubItem = {
      title: 'Tobacco',
      location: networkId
        ? retailerNetworkCreator.tobacco(networkId)
        : retailerNetworkCreator.tobaccoDefault(),
      accessControlKey: Feature.RETAILER_NETWORKS_VIEW,
    };

    const performanceItem: NavigationSubItem = {
      title: 'Performance',
      location: networkId
        ? retailerNetworkCreator.performance(networkId)
        : retailerNetworkCreator.performanceDefault(),
      accessControlKey: Feature.RETAILER_NETWORKS_VIEW,
    };

    if (isTobaccoPerformanceEnabled) {
      return [overviewItem, myStoresItem, tobaccoItem, performanceItem];
    }

    return [overviewItem, myStoresItem, performanceItem];
  }, [isRetailerNetworkSiteUserRole, networkId, isTobaccoPerformanceEnabled]);

  return isRetailerConnectEnabled ? (
    <NestedNav
      title={RETAILER_CONNECT_TITLE}
      icon={<RetailerConnectIcon />}
      items={items}
      baseUrl={RETAILER_CONNECT_BASE_URL}
    />
  ) : null;
};
